import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, X, Star, RefreshCcw, MapPin, Phone, Mail, Globe, AlertTriangle, Building2, Info } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading } = useAuth() || {};
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [sugFilter, setSugFilter] = useState("pending");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/dashboard").then(r => setStats(r.data)).catch(() => {});
      api.get("/admin/stories", { params: { status: filter } }).then(r => setStories(r.data || [])).catch(() => {});
      api.get("/admin/institutions/suggestions", { params: { status: sugFilter } })
        .then(r => setSuggestions(r.data || [])).catch(() => setSuggestions([]));
    }
  }, [user, filter, sugFilter, reload]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-32 text-[#A0AABF]">Se încarcă...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const action = async (sid, act) => {
    try {
      await api.post(`/admin/stories/${sid}/${act}`);
      toast.success("Acțiune efectuată");
      setReload(r => r + 1);
    } catch (e) {
      toast.error("Eroare");
    }
  };

  const sugAction = async (sid, act) => {
    try {
      await api.post(`/admin/institutions/suggestions/${sid}/${act}`);
      toast.success(act === "approve" ? "Sugestie aprobată" : "Sugestie respinsă");
      setReload(r => r + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  return (
    <div data-testid="page-admin" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <header className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] text-[#D4A63D] uppercase mb-4">Control Center</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#F8F8F4]">Admin Panel</h1>
        </div>
        <button onClick={() => setReload(r => r + 1)} className="btn-ghost-light rounded-sm px-4 py-2 text-sm inline-flex items-center gap-2"><RefreshCcw className="w-4 h-4" /> Reîmprospătează</button>
      </header>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
          {[
            { l: "Utilizatori", v: stats.users },
            { l: "Povești pending", v: stats.stories_pending },
            { l: "Sesizări", v: stats.tips },
            { l: "Bookings", v: stats.bookings_pending },
            { l: "Donații", v: stats.donations_paid },
            { l: "Comenzi", v: stats.orders },
          ].map(s => (
            <div key={s.l} className="cinematic-card rounded-lg p-4 text-center">
              <div className="font-display text-3xl gold-text">{s.v}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#A0AABF] mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl text-[#F8F8F4]">Povești — moderare</h2>
          <div className="flex gap-2">
            {["pending", "approved", "rejected"].map(s => (
              <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm border ${filter === s ? "border-[#D4A63D] text-[#D4A63D]" : "border-white/10 text-[#A0AABF]"}`}>{s}</button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {stories.length === 0 && <p className="text-[#A0AABF] italic">Nicio poveste în {filter}.</p>}
          {stories.map(s => (
            <article key={s.story_id} className="cinematic-card rounded-lg p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4A63D] mb-1">{s.county} · {s.relation} · {s.category}</div>
                  <h3 className="font-display text-lg text-[#F8F8F4]">{s.title}</h3>
                  <p className="text-sm text-[#A0AABF] mt-2 line-clamp-3">{s.body}</p>
                  <p className="text-xs text-[#6C7A9C] mt-2">{new Date(s.created_at).toLocaleString("ro-RO")} · {s.visibility}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {filter === "pending" && (
                    <>
                      <button onClick={() => action(s.story_id, "approve")} className="p-2 rounded-sm bg-[#3BA76D]/20 text-[#3BA76D] hover:bg-[#3BA76D]/30" title="Aprobă"><Check className="w-4 h-4" /></button>
                      <button onClick={() => action(s.story_id, "reject")} className="p-2 rounded-sm bg-[#C83A3A]/20 text-[#C83A3A] hover:bg-[#C83A3A]/30" title="Respinge"><X className="w-4 h-4" /></button>
                    </>
                  )}
                  <button onClick={() => action(s.story_id, "feature")} className="p-2 rounded-sm bg-[#D4A63D]/20 text-[#D4A63D] hover:bg-[#D4A63D]/30" title="Featured"><Star className="w-4 h-4" /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* INSTITUTION SUGGESTIONS — MODERATION */}
      <section className="mt-14" data-testid="admin-suggestions-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl text-[#F8F8F4] inline-flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#D4A63D]" /> Sugestii instituții — moderare
            </h2>
            <p className="text-xs text-[#A0AABF] mt-1">Adăugări și corecții propuse de utilizatori. La aprobare, „add” inserează instituția în registru.</p>
          </div>
          <div className="flex gap-2">
            {["pending", "approved", "rejected"].map(s => (
              <button key={s} data-testid={`admin-sug-filter-${s}`} onClick={() => setSugFilter(s)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm border ${sugFilter === s ? "border-[#D4A63D] text-[#D4A63D]" : "border-white/10 text-[#A0AABF]"}`}>{s}</button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {suggestions.length === 0 && <p className="text-[#A0AABF] italic">Nicio sugestie în {sugFilter}.</p>}
          {suggestions.map(s => (
            <article
              key={s.suggestion_id}
              data-testid={`admin-sug-card-${s.suggestion_id}`}
              className="cinematic-card rounded-lg p-5"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span
                      className={`text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded border ${
                        s.action === "add"
                          ? "bg-[#3BA76D]/15 text-[#3BA76D] border-[#3BA76D]/40"
                          : "bg-[#E89BB8]/15 text-[#E89BB8] border-[#E89BB8]/40"
                      }`}
                    >
                      {s.action === "add" ? "Adăugare" : "Corecție"}
                    </span>
                    {s.kind && (
                      <span className="text-[10px] tracking-wider uppercase text-[#A0AABF] px-2 py-0.5 rounded border border-white/10">
                        {s.kind}
                      </span>
                    )}
                    <span className="text-[10px] tracking-widest uppercase text-[#6C7A9C] inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {s.county}
                    </span>
                  </div>
                  <h3 className="font-display text-lg text-[#F8F8F4]">
                    {s.name || (s.action === "correct" ? `Corecție pentru instituția ${s.existing_inst_id}` : "(fără nume)")}
                  </h3>

                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#A0AABF]">
                    {s.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                    {s.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</span>}
                    {s.website && (
                      <a href={s.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#E89BB8] hover:underline">
                        <Globe className="w-3 h-3" /> {s.website}
                      </a>
                    )}
                    {s.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.address}</span>}
                  </div>

                  {s.notes && (
                    <div className="mt-3 p-3 rounded-sm bg-[#0A1020] border border-white/5 text-sm text-[#F8F8F4]/90">
                      <div className="text-[10px] tracking-widest uppercase text-[#A0AABF] font-bold mb-1 flex items-center gap-1">
                        <Info className="w-3 h-3" /> {s.action === "correct" ? "Ce trebuie corectat" : "Note utilizator"}
                      </div>
                      <p className="whitespace-pre-line leading-relaxed">{s.notes}</p>
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-[#6C7A9C]">
                    <span>Trimis: {new Date(s.created_at).toLocaleString("ro-RO")}</span>
                    {s.submitter_email && <span>· contact: <span className="text-[#A0AABF]">{s.submitter_email}</span></span>}
                    {s.existing_inst_id && <span>· ref: <span className="text-[#A0AABF]">{s.existing_inst_id}</span></span>}
                  </div>
                </div>

                {sugFilter === "pending" && (
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      data-testid={`admin-sug-approve-${s.suggestion_id}`}
                      onClick={() => sugAction(s.suggestion_id, "approve")}
                      className="p-2 rounded-sm bg-[#3BA76D]/20 text-[#3BA76D] hover:bg-[#3BA76D]/30"
                      title={s.action === "add" ? "Aprobă și inserează în registru" : "Marchează drept revizuit"}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`admin-sug-reject-${s.suggestion_id}`}
                      onClick={() => sugAction(s.suggestion_id, "reject")}
                      className="p-2 rounded-sm bg-[#C83A3A]/20 text-[#C83A3A] hover:bg-[#C83A3A]/30"
                      title="Respinge"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {sugFilter !== "pending" && (
                  <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded shrink-0 ${
                    sugFilter === "approved" ? "bg-[#3BA76D]/15 text-[#3BA76D]" : "bg-[#C83A3A]/15 text-[#C83A3A]"
                  }`}>
                    {sugFilter === "approved" ? "Aprobat" : "Respins"}
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
